import { DndContext, closestCenter } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import IonIcon from '@reacticons/ionicons'
import { useEffect, useState, useRef } from 'react'
import {
  updateconvoflowbyposition,
  updateconvoflow
} from '../../../services/data'
import validateJSON from '../../../util/jsonvalidation'
import { useLocation, useParams } from 'react-router-dom'
import { rebuildChatbot } from '../../../util/loadExternalScript'

const DraggableConvoList = ({
  getconvolist,
  setGetconvolist,
  handlegetexistconvo,
  handleScroll
}) => {
  const [sortedList, setSortedList] = useState([])
  const location = useLocation()
  const params = useParams()

  const scrollContainerRef = useRef(null) // ref for scroll container

  useEffect(() => {
    if (scrollContainerRef.current && handleScroll) {
      const container = scrollContainerRef.current
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const getStoreIdFromUrl = () => {
    if (params?.store_id) return params.store_id
    const pathParts = location.pathname.split('/')
    console.log(pathParts[3])
    return pathParts[3] || null
  }

  const store_id = getStoreIdFromUrl()

  // Normalize status: ensure only "active"/"inactive" strings exist
  const normalizeActive = it => ({
    ...it,
    status:
      it.status === 'active' || it.is_status === 'active'
        ? 'active'
        : 'inactive'
  })

  useEffect(() => {
    setSortedList(
      [...getconvolist]
        .map(normalizeActive)
        .sort((a, b) => a.position - b.position)
    )
  }, [getconvolist])

  // Listen for custom rebuild event
  useEffect(() => {
    const handleRebuild = () => {
      console.log('Custom event received → calling rebuildChatbot()')
      rebuildChatbot()
    }

    window.addEventListener('chatbotRebuildNeeded', handleRebuild)
    return () => {
      window.removeEventListener('chatbotRebuildNeeded', handleRebuild)
    }
  }, [])

  const handleToggleActive = async flow => {
    const prevStatus = flow.status === 'active'
    const nextStatus = prevStatus ? 'inactive' : 'active'

    setSortedList(list =>
      list.map(it => (it.id === flow.id ? { ...it, status: nextStatus } : it))
    )
    setGetconvolist(list =>
      list.map(it => (it.id === flow.id ? { ...it, status: nextStatus } : it))
    )

    const payload = {
      id: flow.id,
      store_id: flow.store_id,
      config:
        typeof flow.config === 'string'
          ? flow.config
          : JSON.stringify(flow.config),
      position: flow.position,
      status: nextStatus
    }

    try {
      const res = await updateconvoflow(
        payload.id,
        payload.position,
        payload.config,
        payload.status,
        payload.meta
      )
      if (!res) throw new Error('No response updating status')
      rebuildChatbot()
    } catch (e) {
      console.error('Error toggling status:', e)
      // rollback on error
      setSortedList(list =>
        list.map(it =>
          it.id === flow.id ? { ...it, status: flow.status } : it
        )
      )
      setGetconvolist(list =>
        list.map(it =>
          it.id === flow.id ? { ...it, status: flow.status } : it
        )
      )
    }
  }

  const handleDragEnd = async event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = sortedList.findIndex(item => item.id === active.id)
    const newIndex = sortedList.findIndex(item => item.id === over.id)

    const updatedList = arrayMove(sortedList, oldIndex, newIndex).map(
      (item, index) => ({
        ...item,
        position: index
      })
    )

    // Optimistically update UI
    setSortedList(updatedList)
    setGetconvolist(updatedList)

    const formattedOutput = {
      store_id: store_id,
      positions: Object.fromEntries(
        updatedList.map(item => [item.id, item.position])
      )
    }

    try {
      const response = await updateconvoflowbyposition(formattedOutput)
      console.log('updateconvoflowbyposition response:', response)

      if (response?.success) {
        // Dispatch custom event instead of calling rebuildChatbot() directly
        const evt = new CustomEvent('chatbotRebuildNeeded')
        window.dispatchEvent(evt)
      } else {
        console.warn('update positions failed:', response)
      }
    } catch (error) {
      console.error('Error updating positions:', error)
    }
  }

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={sortedList.map(item => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div
          ref={scrollContainerRef}
          className='py-2 scrollbar-hide overflow-y-auto h-full relative'
        >
          {sortedList.map(item => (
            <SortableItem
              key={item.id}
              item={item}
              handlegetexistconvo={handlegetexistconvo}
              onToggleActive={() => handleToggleActive(item)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

const SortableItem = ({ item, handlegetexistconvo, onToggleActive }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  let title = 'Untitled'
  try {
    const config =
      item.config && typeof item.config === 'object'
        ? item.config
        : validateJSON(item.config)
    title = config.label || 'Untitled'
  } catch (error) {
    console.warn('Invalid JSON in item.config:', item.config)
  }

  const isActive = item.status === 'active'

  return (
    <div
      onClick={() => handlegetexistconvo(item.id)}
      ref={setNodeRef}
      style={style}
      className='group w-full md:max-w-[350px] hover:shadow-md px-3 py-3 mt-2 flex justify-between items-center rounded-lg bg-white cursor-pointer'
    >
      <div
        {...listeners}
        {...attributes}
        className='p-2 cursor-grab flex gap-2 items-center'
      >
        <IonIcon
          name='reorder-three-outline'
          className='text-[#a7a7a7] group-hover:text-[#000]'
          aria-hidden='true'
        />
        <p
          dangerouslySetInnerHTML={{ __html: title }}
          className='mr-2 text-[12px] leading-5'
        ></p>
      </div>

      <div className='flex items-center gap-1'>
        {/* ON/OFF switch */}
        <button
          type='button'
          role='switch'
          aria-checked={isActive}
          aria-label={isActive ? 'Turn off flow' : 'Turn on flow'}
          title={isActive ? 'On' : 'Off'}
          onClick={e => {
            e.stopPropagation()
            onToggleActive(item.id, isActive)
          }}
          onMouseDown={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
            isActive ? 'bg-green-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
              isActive ? 'translate-x-4' : 'translate-x-1'
            }`}
            aria-hidden='true'
          />
        </button>

        <button
          className='p-2'
          onClick={e => {
            e.stopPropagation()
            handlegetexistconvo(item.id)
          }}
          aria-label='Edit flow'
          title='Edit'
        >
          <IonIcon
            name='create-outline'
            className='text-[#a7a7a7] group-hover:text-[#000]'
            aria-hidden='true'
          />
        </button>
      </div>
    </div>
  )
}

export default DraggableConvoList
